// 1. Angular Core e Framework
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

// 2. PO-UI (Componentes e Módulos)
import {
  PoButtonModule,
  PoDialogService,
  PoFieldModule,
  PoInputComponent,
  PoLoadingModule,
  PoModalComponent,
  PoModalModule,
  PoPageModule,
  PoRadioGroupModule,
  PoTableModule,
} from '@po-ui/ng-components';

import { ToasterComponent } from '../../shared/components/toaster/toaster.component';

// 4. Serviços (Services)
import { CountService } from '../../shared/service/count.service';
import { ManagementService } from '../../shared/service/management.service';
import { NotificationService } from '../../shared/service/notification.service';
import { TokenService } from '../../shared/service/token.service';

// 5. Modelos (Models / Types)
import { PostFinalizationCountBody } from '../../models/finalization-count.model';
import { GetBranchParameterResponse } from '../../models/management.model';
import { ToasterService } from '../../shared/service/toaster.service';
import { CountStatus, CountStatusLabel } from '../../enums/count-status.enum';

@Component({
  standalone: true,
  selector: 'app-production-order-finalization',
  templateUrl: './production-order-finalization.component.html',
  styleUrls: ['./production-order-finalization.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoTableModule,
    PoButtonModule,
    PoModalModule,
    PoFieldModule,
    PoLoadingModule,
    PoRadioGroupModule,
    ToasterComponent,
    ReactiveFormsModule,
  ],
})
export class ProductionOrderFinalizationComponent implements OnInit, OnDestroy {
  @ViewChild('countModal') countModal!: PoModalComponent;
  @ViewChild('cycleInput') cycleInput!: PoInputComponent;
  @ViewChild('orderInput') orderInput!: PoInputComponent;

  countForm!: FormGroup;
  isLoading = true;
  items: any[] = [];
  readonly singleReadingOptions = [
    { label: 'Sim', value: 1 },
    { label: 'Não', value: 0 },
  ];

  readonly columns = [
    {
      property: 'code',
      label: 'Código',
    },
    {
      property: 'description',
      label: 'Descrição',
      width: '40%',
    },
    {
      property: 'itensCount',
      label: 'Qtd',
    },
    {
      property: 'orderCode',
      label: 'Ciclo / OP',
    },
    {
      property: 'statusDescription',
      label: 'Status',
    },
    {
      property: 'action',
      label: ' ',
      type: 'cellTemplate',
      width: '10%',
    },
  ];

  private readonly countService = inject(CountService);
  private readonly tokenService = inject(TokenService);
  private readonly managementService = inject(ManagementService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly poDialog = inject(PoDialogService);
  private readonly fb = inject(FormBuilder);
  private readonly toasterService = inject(ToasterService);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.countForm = this.fb.group({
      countDescription: [''],
      isSingleReading: [0],
      cycleCode: [{ value: '', disabled: true }],
      orderCode: [{ value: '', disabled: true }],
    });

    this.countForm.get('isSingleReading')?.valueChanges.subscribe(isSingle => {
      this.toggleFields(isSingle);
    });

    this.notificationService.finalizationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.loadOutputCounts();
      });

    this.loadOutputCounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal(): void {
    this.countForm.reset();
    this.initializeFormPreferences();
    this.countModal.open();
  }

  saveCount(): void {
    const branchCode = Number(this.tokenService.getBranch());
    const userCode = this.tokenService.getUserCode();
    const formValues = this.countForm.getRawValue();

    const body: PostFinalizationCountBody = {
      branchCode,
      component: this.tokenService.getComponentCode('production-order-finalization')!,
      description: formValues.countDescription,
      productionOrderCycleCode: formValues.cycleCode,
      productionOrderCode: formValues.orderCode,
    };

    const storageData = {
      isSingleReading: formValues.isSingleReading,
      branchCode: branchCode,
      userCode: userCode,
    };

    localStorage.setItem('lastCountPreference', JSON.stringify(storageData));

    this.countService.postFinalizationCount(body).subscribe({
      next: ({ code }) => {
        this.countModal.close();

        this.router.navigate(['/finalization-count-items'], {
          state: {
            countDetails: {
              branchCode: branchCode,
              code,
              description: formValues.countDescription,
              isSingleReading: formValues.isSingleReading,
            },
          },
        });
      },
      error: err => {
        const message = err?.error?.[0]?.message || 'Falha na criação da contagem.';
        this.toasterService.triggerAlert(message, 'error');
        console.error('Erro ao criar nova contagem:', err);
      },
    });
  }

  closeModal(): void {
    this.countModal.close();
  }

  goToCountDetails(branchCode: number, code: number, description: string): void {
    this.router.navigate(['/finalization-count-items'], {
      state: {
        countDetails: {
          branchCode: branchCode,
          code: code,
          description: this.countForm.get('countDescription')?.value || description || '',
        },
      },
    });
  }

  cancelCount(branchCode: number, code: number): void {
    this.isLoading = true;

    this.countService.postUpdateCountStatus(branchCode, code, 6).subscribe({
      next: () => {
        this.isLoading = false;
        this.items = this.items.filter(item => item.code !== code);
      },
      error: err => {
        this.isLoading = false;
        console.error('Erro ao cancelar contagem:', err);
      },
    });
  }

  confirmDeleteCount(branchCode: number, code: number): void {
    this.poDialog.confirm({
      title: 'Confirmar cancelamento',
      message: `Deseja realmente cancelar a contagem ${code}?`,
      confirm: () => this.cancelCount(branchCode, code),
    });
  }

  private toggleFields(isSingle: number) {
    const cycle = this.countForm.get('cycleCode');
    const order = this.countForm.get('orderCode');
    const currentBranch = Number(this.tokenService.getBranch());

    if (isSingle) {
      this.managementService
        .getBranchParameter({
          branchCodeList: [currentBranch],
          parameterCodeList: ['NR_CICLO_ATUAL', 'IN_DIGITA_CICLO'],
        })
        .subscribe({
          next: (response: GetBranchParameterResponse) => {
            const getParamValue = (code: string) => {
              const param = response?.items?.find(p => p.parameterCode === code);

              if (!param) return undefined;

              const branchValue = param.values?.find(
                v => Number(v.branchCode) === currentBranch,
              )?.value;

              return branchValue ?? param.globalValue;
            };

            const nrCicloAtual = getParamValue('NR_CICLO_ATUAL');
            const inDigitaCiclo = getParamValue('IN_DIGITA_CICLO');

            this.enableFields(cycle, order);

            if (nrCicloAtual) {
              cycle?.setValue(nrCicloAtual);
            }

            this.focusField(inDigitaCiclo, nrCicloAtual);
          },
          error: err => {
            console.error('Erro ao buscar parâmetros:', err);
            cycle?.enable();
            order?.enable();
          },
        });
    } else {
      this.resetFields(cycle, order);
    }
  }

  private focusField(
    inDigitaCiclo: string | null | undefined,
    nrCicloAtual: string | null | undefined,
  ) {
    setTimeout(() => {
      if (inDigitaCiclo === '0') {
        if (nrCicloAtual) {
          this.orderInput.focus();
        } else {
          this.cycleInput.focus();
        }
      } else {
        this.cycleInput.focus();
      }
    }, 200);
  }

  private enableFields(cycle: AbstractControl | null, order: AbstractControl | null) {
    cycle?.enable();
    order?.enable();
    cycle?.setValidators([Validators.required]);
    order?.setValidators([Validators.required]);
    cycle?.updateValueAndValidity();
    order?.updateValueAndValidity();
  }

  private resetFields(cycle: AbstractControl | null, order: AbstractControl | null) {
    cycle?.disable();
    order?.disable();
    cycle?.setValue('');
    order?.setValue('');
    cycle?.clearValidators();
    order?.clearValidators();
    cycle?.updateValueAndValidity();
    order?.updateValueAndValidity();
  }

  private loadOutputCounts(): void {
    this.isLoading = true;
    const branchCode = Number(this.tokenService.getBranch()!);
    this.countService
      .getPendingOutputCounts(
        branchCode,
        this.tokenService.getComponentCode('production-order-finalization')!,
      )
      .subscribe({
        next: response => {
          this.items = (response.items || []).map((item: any) => {
            const isProcessing = this.notificationService.isProcessing(item.code);
            return {
              ...item,
              orderCode:
                item.productionOrderCycleCode && item.productionOrderCode
                  ? `${item.productionOrderCycleCode} / ${item.productionOrderCode}`
                  : '',
              statusDescription: isProcessing
                ? CountStatusLabel[CountStatus.Processing]
                : CountStatusLabel[item.status as keyof typeof CountStatusLabel] || 'Desconhecido',
            };
          });
          this.isLoading = false;
        },
        error: err => {
          console.error('Erro ao carregar contagens:', err);
          this.isLoading = false;
          this.items = [];
        },
      });
  }

  private initializeFormPreferences(): void {
    const savedData = localStorage.getItem('lastCountPreference');
    const currentBranch = Number(this.tokenService.getBranch());
    const currentUser = this.tokenService.getUserCode();

    if (savedData) {
      try {
        const { isSingleReading, branchCode, userCode } = JSON.parse(savedData);

        if (branchCode === currentBranch && userCode === currentUser) {
          this.countForm.patchValue({ isSingleReading });
          return;
        }
      } catch (error) {
        console.error('Erro ao ler preferências do localStorage', error);
      }
    }

    this.countForm.patchValue({ isSingleReading: 0 });
  }
}
